import { Options, Vue } from 'vue-class-component';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons/faCheckCircle';
import { faBolt } from '@fortawesome/free-solid-svg-icons/faBolt';
import { Adb } from '@/api/adb/adb';
import { DeviceType, LicenseType, PicoG3Device } from '@/entities/device';
import { Config, Configuration, Files } from '@/entities/configuration';
import { AdbDaemonWebUsbDeviceManager } from '@yume-chan/adb-daemon-webusb';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import {
    filterFiles,
    filterUpToDate,
    findParametersFiles,
    isFilesAuthorized,
    PARAMETERS_FILE
} from '@/utility/devices';
import { logger } from '@/api/logger';
import { timeConversion } from '@/utility/time';
import { getConfigUrl } from '@/utility/config';
import { isUsbTransferError } from '@/utility/usb';

@Options({
    props: {
        serial: String
    },
    methods: {
        check() {
            return faCheckCircle;
        },
        warning() {
            return faBolt;
        }
    },
    components: { FontAwesomeIcon }
})
export default class PicoG3 extends Vue {
    declare public serial: string;
    declare public check: () => IconDefinition;
    declare public warning: () => IconDefinition;
    public device: PicoG3Device | null = null;
    public configuration: Configuration | null = null;
    public deviceFiles: Files[] = [];
    public allKnownPackages: string[] = [];
    public deviceLoaded = false;
    public deviceReady = false;
    public configApplied = false;
    public status = 'Chargement de la configuration';
    public error: string | null = null;
    public progress: number | null = null;
    public displaySetupLog = false;
    public displayRebootConfirm = false;
    public displayRebootDone = false;
    public override = false;
    public overrideLargesFiles = false;

    private handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') this.dismissReboot();
    };

    /**
     * La fin de setup est memorisee hors du composant : AutoConfig le demonte des qu'un
     * evenement USB arrive (`handleDevice`), et le casque se re-enumere justement dans les
     * secondes qui suivent un setup. Sans ca, la boite de dialogue disparaissait sous les
     * doigts de l'utilisateur avant qu'il ait pu cliquer.
     */
    private get pendingKey(): string {
        return `pendingReboot:${this.serial}`;
    }

    private get pendingReboot(): number | null {
        try {
            const raw = sessionStorage.getItem(this.pendingKey);
            if (!raw) return null;
            const at = Number.parseInt(raw);
            // Un marqueur oublie (onglet laisse ouvert, casque debranche) ne doit pas
            // ressusciter la dialog des heures plus tard.
            if (Number.isNaN(at) || Date.now() - at > 15 * 60 * 1000) {
                this.clearPendingReboot();
                return null;
            }
            return at;
        } catch {
            return null;
        }
    }

    private markPendingReboot(): void {
        try {
            sessionStorage.setItem(this.pendingKey, String(Date.now()));
        } catch {
            // navigation privee ou stockage refuse : on garde juste l'etat en memoire
        }
    }

    private clearPendingReboot(): void {
        try {
            sessionStorage.removeItem(this.pendingKey);
        } catch {
            // rien a nettoyer
        }
    }

    mounted() {
        this.getDevice().catch(console.error);
        this.$watch('override', () => {
            if (!this.override) this.overrideLargesFiles = false;
        });
        window.addEventListener('keydown', this.handleEscape);
    }

    unmounted() {
        window.removeEventListener('keydown', this.handleEscape);
        // Ne pas remettre `device`/`configuration` a null : un `setupDevice()` encore en vol
        // continue de s'en servir. AutoConfig demonte ce composant des qu'un evenement USB
        // add/remove arrive (`handleDevice`), ce qui se produit quand le casque se
        // re-enumere pendant une installation. Les nuller transformait une erreur USB
        // recuperable en cascade de TypeError, y compris dans le `catch`/`finally` du setup.
        // Le composant est detruit de toute facon : ces champs disparaissent avec lui.
        this.deviceLoaded = false;
        this.deviceReady = false;
        this.configApplied = false;
        this.status = 'Chargement de la configuration';
        this.progress = null;
        this.override = false;
        this.overrideLargesFiles = false;
        // L'etat reel vit dans `sessionStorage` : il est restaure au remontage.
        this.displayRebootConfirm = false;
        this.displayRebootDone = false;
    }

    updateProgress(e: ProgressEvent) {
        const pc = (e.loaded * 100) / e.total;
        this.progress = pc == 100 ? null : pc;
    }

    hasRequestVersion() {
        if (!this.device || !this.configuration) return true;
        const dsv = Number(this.device.systemVersion.replaceAll('.', ''));
        const rsv = Number(this.configuration.minVersionFirmware.replaceAll('.', ''));
        return dsv >= rsv;
    }

    async resetDevice() {
        if (this.device == null) throw 'Device is null';
        if (this.configuration == null) throw 'Configuration is null';
        this.deviceReady = false;
    }

    private async getDevice() {
        try {
            const manager = AdbDaemonWebUsbDeviceManager.BROWSER;
            if (!manager) throw "Le périphérique USB n'est pas disponible";
            const device = (await manager.getDevices()).find((d) => d.serial == this.serial);
            if (device != null) {
                this.device = new PicoG3Device(device.name, device.serial, device);
                await this.loadDeviceInfo();
            }
        } catch (e) {
            logger.error({ action: 'load', serial: this.serial, error: `${e}` });
            this.device = null;
        } finally {
            this.deviceLoaded = true;
            this.status = '';
        }
    }

    private async loadDeviceInfo() {
        if (this.device == null) return;
        // La config d'abord : elle designe la source du `parameters.pem` de ce casque
        // (config par casque prioritaire), que `loadInfo` compare au fichier present
        // sur le casque dans la meme connexion ADB que la licence.
        await this.loadConfig(DeviceType.PicoG3, this.serial);
        this.device.parametersSource = findParametersFiles(this.deviceFiles, this.configuration);
        await this.device.loadInfo();
        await this.loadPackagesInfo();
        await this.deviceIsReady();
        this.restoreRebootState();
    }

    /**
     * Un setup s'est termine avant ce (re)montage : soit le casque a redemarre entre-temps
     * - Bliss Home le fait de lui-meme a la fin de son propre setup - et il n'y a plus rien
     * a proposer, soit il tourne toujours et on represente le bouton.
     */
    private restoreRebootState() {
        const at = this.pendingReboot;
        if (at == null || this.device == null) return;
        if (this.device.hasRebootedSince(at)) {
            this.clearPendingReboot();
            this.displayRebootConfirm = false;
            this.displayRebootDone = true;
            logger.debug({
                action: 'reboot detected',
                serial: this.serial,
                uptime: this.device.uptime
            });
            return;
        }
        this.displayRebootConfirm = true;
    }

    private async loadConfig(deviceType: DeviceType, serial: string) {
        this.configuration = null;
        this.deviceFiles = [];
        try {
            const resp = await fetch(getConfigUrl());
            const config = (await resp.json()) as Config;
            this.allKnownPackages = config.configs
                .map((c) => c.applications.packages)
                .map((pkgs) => pkgs.map((p) => p.pkg))
                .reduce((pkgs, value) => pkgs.concat(value), []);
            const configs = config.configs.filter((c) => c.deviceType == deviceType.toString());
            if (serial) {
                const device = config.devices.find((d) => d.serial == serial);
                if (device) {
                    this.configuration = configs.find((c) => c.id == device.config) ?? null;
                    this.deviceFiles = device.files ?? [];
                }
            }
            if (!this.configuration) {
                this.configuration = configs.find((c) => c.visibility == 'public') ?? null;
            }
        } catch (e: any) {
            this.error = 'Impossible de charger les configurations';
        }
    }

    private async loadPackagesInfo() {
        if (!this.device || !this.configuration) return;
        const pkgs = this.configuration.applications.packages.map((p) => p.pkg);
        await this.device.loadPackagesInfo(pkgs);
    }

    private async deviceIsReady() {
        if (!this.device || !this.configuration) return;
        this.configApplied = this.device.currentConfig == this.configuration.name;
        if (!this.device.hasDeviceLicense) this.deviceReady = false;
        else if (this.device.hasLicenseToUpdate) this.deviceReady = false;
        else if (this.device.hasParametersToUpdate) this.deviceReady = false;
        else if (!this.device.hasTimeUpToDate) this.deviceReady = false;
        else this.deviceReady = this.configApplied;
    }

    async rebootDevice() {
        if (this.device == null) throw 'Device is null';
        await this.device.reboot();
    }

    async setupDevice() {
        if (this.device == null) throw 'Device is null';
        if (this.configuration == null) throw 'Configuration is null';
        const startTime = Date.now();
        // Captures locales : le `catch`/`finally` doit pouvoir journaliser meme si le
        // composant a ete demonte entre-temps.
        const serial = this.device.serial;
        const configName = this.configuration.name;
        logger.debug({
            action: 'setup start',
            serial: serial,
            configuration: configName
        });
        this.status = 'Configuration en cours';
        const adb = new Adb(this.device.serial);
        let isOnError = false;
        try {
            await adb.connect();

            await this.uninstallUnnecessaryApps(adb);

            // Config Props & Files
            for (const props in this.configuration.props) {
                this.status = "Configuration d'Android";
                const old = await this.device.getProps(adb, props);
                const value = this.configuration.props[props];
                if (old != value) await this.device.setProps(adb, props, value);
            }
            if (this.configuration.files) {
                await this.pushFiles(adb, this.configuration.files);
            }

            // Config APPS
            const baseUrl = this.configuration.applications.baseUrl;
            for (const app of this.configuration.applications.packages) {
                console.log(`installable ${app.pkg} => ${this.device.authorizedInstall(app)}`);
                if (!this.device.authorizedInstall(app)) continue;
                // Une app "virtuelle" ne declare pas d'`apks` : elle n'existe que pour rattacher
                // des fichiers a une entree de licence (les localisations Endocare, par
                // exemple). Rien a installer ni a autoriser, on passe directement a ses `files`.
                if (app.apks?.length) {
                    const code = this.device.getVersionCode(app.apks);
                    console.log(
                        `installed ${app.pkg}: ${code} => ${this.device.isInstalled(app.pkg, code)}`
                    );
                    if (this.override || !this.device.isInstalled(app.pkg, code)) {
                        this.status = `Installation de ${app.name}`;
                        adb.addEventListener('progress', this.updateProgress);
                        try {
                            await this.device.installBundle(
                                adb,
                                baseUrl,
                                app.pkg,
                                app.apks,
                                app.adminReceiver != ''
                            );
                        } catch (e) {
                            // Seul `install failed` (pm a refuse le paquet) justifie une
                            // reinstallation. Toute autre erreur - typiquement une coupure
                            // USB en plein push - doit interrompre le setup : l'avaler
                            // laissait la boucle continuer et le setup se terminer en
                            // "complete" alors que l'app n'etait pas installee.
                            if ('install failed' == `${e}`) {
                                if (app.adminReceiver) {
                                    const rcv = `${app.pkg}/.${app.adminReceiver}`;
                                    await this.device.configureDeviceAdmin(adb, rcv, false);
                                }
                                await this.device.uninstall(adb, app.pkg);
                                await this.device.installBundle(
                                    adb,
                                    baseUrl,
                                    app.pkg,
                                    app.apks,
                                    app.adminReceiver != ''
                                );
                            } else {
                                throw e;
                            }
                        }
                        adb.removeEventListener('progress', this.updateProgress);
                        this.progress = null;
                    }
                    const pkg = this.device.packages.find((p) => p.name == app.pkg);
                    const installedPerms = pkg ? pkg.permissions : [];
                    if (app.permissions) {
                        const perms = app.permissions.filter(
                            (p) => this.override || installedPerms.indexOf(p) == -1
                        );
                        for (const perm of perms) {
                            this.status = `Configuration des permissions de ${app.name}`;
                            console.log('set permission', app.pkg, perm);
                            await this.device.setPermission(adb, app.pkg, perm);
                        }
                    }
                }
                if (app.files) {
                    await this.pushFiles(adb, app.files);
                }
            }

            console.log('all applications installed');

            const licType = this.getLicenseType();
            const pkgPrefix = this.getPackagePrefix(licType);

            console.log(`license type: ${licType}, prefix: ${pkgPrefix}`);

            console.log(`device has server license ${this.device.hasServerLicense}`);
            if (this.device.hasServerLicense)
                console.log(`server license: ${this.device.serverLicense}`);
            console.log(`device has device license ${this.device.hasDeviceLicense}`);
            if (this.device.hasDeviceLicense)
                console.log(`device device: ${this.device.deviceLicense}`);

            if (this.override) {
                console.log('removeUselessFiles & resetDeviceLicense');
                await this.device.removeUselessFiles(adb);
                this.device.resetDeviceLicense();
            }

            if (
                this.device.hasServerLicense &&
                (this.override || !this.device.hasDeviceLicense || this.device.hasLicenseToUpdate)
            ) {
                console.log(`installation de la license : ${licType}`);
                this.status = 'Installation de la licence';
                await this.device.pushDeviceLicense(adb, licType);
            }

            // Fichiers propres au casque : joues en dernier (apres la licence) pour
            // ecraser leur equivalent generique deja copie par la configuration.
            if (this.deviceFiles.length > 0) {
                this.status = 'Copie des fichiers du casque';
                console.log('push device specific files', this.deviceFiles.length);
                await this.pushFiles(adb, this.deviceFiles, true);
            }

            if (this.device.serial == 'PA7R50SGHA200562W') {
                await adb.shell("echo '' > /storage/emulated/0/.updating");
            }

            // Setup
            this.status = 'Configuration finale du casque...';
            console.log('configuration du casque');
            console.log(`- hasTimeUpToDate: ${this.device.hasTimeUpToDate}`);

            if (!this.device.hasTimeUpToDate) {
                console.log(`set device admin: ${pkgPrefix}.maintenance`);
                await this.setDeviceAdmin(adb, `${pkgPrefix}.maintenance`, true);
                console.log(`set time: ${pkgPrefix}.maintenance`);
                await this.device.setDatetime(adb, licType);
            }
            console.log(`remove device admin: ${pkgPrefix}.maintenance`);
            await this.setDeviceAdmin(adb, `${pkgPrefix}.maintenance`, false);

            console.log(`write config name: ${this.configuration.name}`);
            await this.device.writeConfigName(adb, this.configuration.name);

            console.log('setup bliss', this.override || this.device.setupLog == '');
            if (this.override || this.device.setupLog == '') {
                const app = this.configuration.applications.packages.find(
                    (app) => app.pkg == `${pkgPrefix}.bliss.home`
                );
                console.log(`- ${app}`);
                if (app) {
                    this.status = `Setup ${app.name}`;
                    await this.device.setupBlissOne(adb, licType);
                }
            }
        } catch (e) {
            console.error(e);
            logger.error({
                action: 'setup error',
                serial: serial,
                configuration: configName,
                error: `${e}`,
                duration: timeConversion(Date.now() - startTime)
            });
            isOnError = true;
        } finally {
            logger.debug({
                action: 'setup complete',
                serial: serial,
                configuration: configName,
                duration: timeConversion(Date.now() - startTime)
            });
            this.status = '';
            this.override = false;
            // Libérer la connexion USB pour que le reboot (nouvelle connexion) puisse l'utiliser
            try {
                await adb.close();
            } catch (e) {
                if (!isUsbTransferError(e)) console.error(e);
            }
        }

        if (!isOnError) {
            this.markPendingReboot();
            this.displayRebootConfirm = true;
        }
    }

    async confirmReboot() {
        this.clearPendingReboot();
        this.displayRebootConfirm = false;
        await this.rebootDevice();
    }

    /** Fermeture manuelle : l'utilisateur a vu le message, on ne le represente plus. */
    dismissReboot() {
        this.clearPendingReboot();
        this.displayRebootConfirm = false;
        this.displayRebootDone = false;
    }

    private getLicenseType(): LicenseType {
        if (this.device == null) throw 'Device is null';
        if (this.configuration == null) throw 'Configuration is null';
        const index = this.configuration.applications.packages.findIndex(
            (p) => p.pkg == 'com.neodtx.bliss.home'
        );
        if (index > -1) return LicenseType.NeoDTx;
        return LicenseType.Butterfly;
    }

    private getPackagePrefix(licType: LicenseType): string {
        if (licType == LicenseType.NeoDTx) return 'com.neodtx';
        return 'com.butterflytx';
    }

    private async uninstallUnnecessaryApps(adb: Adb) {
        if (this.device == null) throw 'Device is null';
        if (this.configuration == null) throw 'Configuration is null';
        try {
            const mustBeRemoved = this.allKnownPackages
                .filter(
                    (pkg) =>
                        this.configuration!.applications.packages.findIndex((p) => p.pkg == pkg) ==
                        -1
                )
                .filter(
                    (pkg) =>
                        pkg.startsWith('com.neodtx') ||
                        pkg.startsWith('com.butterfly') ||
                        pkg.startsWith('com.Enozone') ||
                        pkg.startsWith('com.effetpapillon') ||
                        pkg.startsWith('com.blissdtx')
                );
            for (const pkg of mustBeRemoved) {
                if (this.device.isInstalled(pkg, -1)) {
                    this.status = `Désinstallation de ${pkg}`;
                    await this.device.uninstall(adb, pkg);
                }
            }
        } catch (e) {
            console.error(e);
        }
    }

    /**
     * `force` : les fichiers sont recopies meme s'ils sont deja presents et de la
     * bonne taille. C'est le cas des fichiers specifiques au casque, qui portent le
     * meme nom (et parfois la meme taille) que la version generique deja poussee par
     * la configuration : seule une recopie inconditionnelle garantit l'ecrasement.
     * Les gros fichiers restent proteges par la case `overrideLargesFiles`.
     *
     * Une entree portant un `requires` est ignoree si aucune des applications qu'elle sert
     * n'est autorisee par la licence : ces fichiers n'ont rien a faire sur le casque, et
     * representent l'essentiel du volume copie (voix Endocare).
     */
    private async pushFiles(adb: Adb, files: Files[], force = false) {
        if (this.device == null) throw 'Device is null';
        for (const remote of files) {
            if (!isFilesAuthorized(remote, (pkg) => this.device!.authorizedInstall({ pkg }))) {
                console.log('skip files (hors licence)', remote.path, remote.requires);
                continue;
            }
            const present = (await this.device.getFiles(adb, remote.path)).filter(
                (f) => remote.files.indexOf(f) > -1
            );
            // La taille ne suffit pas pour `parameters.pem` : un nouveau PEM fait
            // souvent la meme longueur que l'ancien. Son contenu a ete compare au
            // chargement, on force sa recopie s'il differe de la config.
            const exists = (await filterUpToDate(adb, remote, present)).filter(
                (f) => f != PARAMETERS_FILE || !this.device!.hasParametersToUpdate
            );
            const missing = filterFiles(
                remote.files,
                exists,
                force || this.override,
                this.overrideLargesFiles
            );
            if (missing.length > 0) {
                await adb.shell(`mkdir -p ${remote.path}`);
                for (const file of missing) {
                    this.status = `Copie de ${file.split('/').pop()}`;
                    const url = `${remote.baseUrl}/${file}`;
                    adb.addEventListener('progress', this.updateProgress);
                    console.log('copy file', file, remote.path);
                    await adb.stream(url, remote.path);
                    adb.removeEventListener('progress', this.updateProgress);
                    this.progress = null;
                }
            }
        }
    }

    private async setDeviceAdmin(adb: Adb, pkg: string, enabled: boolean) {
        if (this.device == null) throw 'Device is null';
        if (this.configuration == null) throw 'Configuration is null';
        if (enabled) {
            const app = this.configuration.applications.packages.find((p) => p.pkg == pkg);
            if (!app || !app.adminReceiver) return;
            const rcv = `${app.pkg}/.${app.adminReceiver}`;
            if (rcv != this.device.deviceAdmin) {
                await this.device.configureDeviceAdmin(adb, rcv, true);
            }
        } else if (this.device.deviceAdmin) {
            const adminPkg = this.device.deviceAdmin.split('/').shift()!;
            if (pkg == adminPkg) {
                await this.device.configureDeviceAdmin(adb, this.device.deviceAdmin, false);
            }
        }
        await adb.wait(100);
    }
}
